import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import InitiatorComponent from './components/InitiatorComponent/InitiatorComponent';
import CollaboratorLandingPageComponent from './components/CollaboratorLandingPage/CollaboratorLandingPage';
import NavBarComponent from './components/NavBar/NavBarComponent';
import LoginComponent from './components/Auth/LoginComponent';
import RegisterComponent from './components/Auth/RegisterComponent';
import ForgotPasswordComponent from './components/Auth/ForgotPasswordComponent';
import RequireAuth from './components/Auth/RequireAuth';
import LogoutComponent from './components/Auth/LogoutComponent';
import AutoFixDisplayName from './debug/AutoFixDisplayName';
import SessionPageComponent from './components/SessionPageComponent/SessionPageComponent';
import AgreeIssueComponent from './components/AgreeIssueComponent/AgreeIssueComponent';
import SessionSolutionsPage from './components/SessionSolutions/SessionSolutionsComponent';
import SecureAccessPage from './components/Auth/SecureAccessPage';

// DPI
import DPITestComponent from './components/DPI/DPITestComponent';
import DPIComponent from './components/DPI/DPIComponent';

// Agreed Issue landing
import IssueLandingComponent from './components/IssueLanding/IssueLandingComponent';

// Resolve
import ResolveComponent from './components/Resolve/ResolveComponent';

// DCS
import DCSModeSelect from './components/DCS/DCSModeSelect';
import DCSQuestionnaire from './components/DCS/DCSQuestionnaire';

// Implement
import ImplementComponent from './components/Implement/ImplementComponent';

// Propose Strategies
import ProposeStrategiesComponent from './components/ProposeStrategies/ProposeStrategiesComponent';


// Home


// HomeComponent (named export) - NEW LANDING PAGE
import { HomeComponent } from './components/Home/HomeComponent';

// Initial Questions
import InitialQuestionsComponent from './components/InitialQuestions/InitialQuestionsComponent';

// Communication (session-scoped)
import CommunicationComponent from './components/Communication/CommunicationComponent';

// Feedback (lazy)
const FeedbackPage = lazy(() => import('./components/Feedback/FeedbackPage'));

/* ---------- Scoped style manager (reference-counted) ---------- */

type StyleRefMap = Record<string, number>;
declare global {
  interface Window {
    __styleRefs__?: StyleRefMap;
  }
}

function ensureRefMap() {
  if (!window.__styleRefs__) window.__styleRefs__ = {};
  return window.__styleRefs__!;
}

function mountStyle(id: string, href: string) {
  const refs = ensureRefMap();
  const current = refs[id] || 0;
  refs[id] = current + 1;

  if (document.getElementById(id)) return; // already present

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

function unmountStyle(id: string) {
  const refs = ensureRefMap();
  const current = refs[id] || 0;
  if (current <= 1) {
    // last user; remove node
    const el = document.getElementById(id);
    if (el) el.remove();
    delete refs[id];
  } else {
    refs[id] = current - 1;
  }
}

// Resolve asset URLs for local CSS at runtime
const THEME_HREF = new URL('./styles/theme.css', import.meta.url).href;
const APP_CSS_HREF = new URL('./App.css', import.meta.url).href;
// Use CDN for Bootstrap to avoid Vite style-injection that cannot be “unimported”
const BOOTSTRAP_HREF =
  'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css';

/** Hook: attach Bootstrap + app styles only while mounted */
function useScopedAppStyles() {
  useEffect(() => {
    mountStyle('bootstrap-css', BOOTSTRAP_HREF);
    mountStyle('app-theme-css', THEME_HREF);
    mountStyle('app-css', APP_CSS_HREF);
    return () => {
      unmountStyle('bootstrap-css');
      unmountStyle('app-theme-css');
      unmountStyle('app-css');
    };
  }, []);
}

/** Smooth-scroll to hash on route changes (e.g., /about#faq) */
function ScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el && 'scrollIntoView' in el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash]);
  return null;
}

/** Wrapper that loads Bootstrap + app styles for its children and cleans up */
function BootstrapWrapper({ children }: { children: React.ReactNode }) {
  useScopedAppStyles();
  return <>{children}</>;
}

/** App shell */
function Shell() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  useScopedAppStyles(); // styles active for all Shell routes

  return (
    <div className={isHome ? undefined : 'page-gradient'}>
      <NavBarComponent />
      <main>
        <Outlet />
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<div className="text-center">Loading...</div>}>
      <ScrollToHash />
      <Routes>
        {/* Landing page OUTSIDE Shell - NO Bootstrap or app styles */}
        <Route path="/" element={<HomeComponent />} />

        {/* Public auth pages - WITH Bootstrap */}
        <Route
          path="/login"
          element={
            <BootstrapWrapper>
              <LoginComponent />
            </BootstrapWrapper>
          }
        />
        <Route
          path="/register"
          element={
            <BootstrapWrapper>
              <RegisterComponent />
            </BootstrapWrapper>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <BootstrapWrapper>
              <ForgotPasswordComponent />
            </BootstrapWrapper>
          }
        />
        <Route
          path="/secure-access"
          element={
            <BootstrapWrapper>
              <SecureAccessPage />
            </BootstrapWrapper>
          }
        />
        <Route
          path="/secure"
          element={
            <BootstrapWrapper>
              <SecureAccessPage />
            </BootstrapWrapper>
          }
        />

        {/* Single protected route outside navbar - WITH Bootstrap */}
        <Route element={<RequireAuth />}>
          <Route
            path="/fix-name"
            element={
              <BootstrapWrapper>
                <AutoFixDisplayName />
              </BootstrapWrapper>
            }
          />
        </Route>

        {/* App layout with NavBar (Shell) - Bootstrap scoped to Shell */}
        <Route element={<Shell />}>
          {/* Public under NavBar */}

          <Route path="feedback" element={<FeedbackPage />} />

          {/* Public NavBar pages */}
          <Route path="init" element={<InitiatorComponent />} />
          <Route path="coll" element={<CollaboratorLandingPageComponent />} />
          <Route path="logout" element={<LogoutComponent />} />

          {/* Protected group */}
          <Route element={<RequireAuth />}>
            <Route path="coll/:id/*" element={<CollaboratorLandingPageComponent />} />

            {/* /session group */}
            <Route path="session">
              <Route index element={<SessionPageComponent key="session-list" />} />
              <Route path=":id" element={<SessionPageComponent key="session-detail" />} />

              {/* Steps under session */}
              <Route path=":id/agree" element={<AgreeIssueComponent key="agree" />} />
              <Route path=":id/solutions" element={<SessionSolutionsPage key="solutions" />} />
              <Route path=":id/agreed-issue" element={<IssueLandingComponent key="agreed" />} />
              <Route path=":id/questions" element={<InitialQuestionsComponent key="question" />} />

              {/* DPI Style routes (session-scoped) */}
              <Route path=":id/communication-style/quiz" element={<DPIComponent />} />
              <Route path=":id/communication-style/test" element={<DPITestComponent />} />

              {/* DCS routes (session-scoped) */}
              <Route path=":id/dcs/test" element={<DCSModeSelect />} />
              <Route path=":id/dcs/quiz" element={<DCSQuestionnaire />} />

              {/* Propose Strategies routes */}
              <Route path=":id/propose-strategies" element={<ProposeStrategiesComponent key="propose" />} />

              {/* Implement route */}
              <Route path=":id/implement" element={<ImplementComponent key="implement" />} />

              {/* Session-scoped resolve */}
              <Route path=":id/resolve" element={<ResolveComponent key="resolve" />} />

              {/* Session-scoped Communication (new) */}
              <Route path=":id/communication" element={<CommunicationComponent key="communication" />} />
            </Route>

            {/* Trailing-slash helper */}
            <Route path="session/" element={<Navigate to="/session" replace />} />

            {/* NON-session DCS routes */}
            <Route path="dcs/test" element={<DCSModeSelect />} />
            <Route path="dcs/quiz" element={<DCSQuestionnaire />} />

            {/* NON-session DPI routes */}
            <Route path="communication-style/quiz" element={<DPIComponent />} />
            <Route path="communication-style/test" element={<DPITestComponent />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
